// DLG-134 Account User Invite and DLG-135 No Location (legacy components/invites/InviteUser.vue with an account,
// AccountsList.vue and AccountsDetail.vue `noLocationAlert`; field note backoffice.accounts.list): from the account card's
// Invite user and Account Detail's Invite New User. "Account: {name}", legacy's description, Email Address and the
// account-users banner; Cancel and Invite (enabled once the email is valid). Persistent, like legacy's. An account with no
// location yet gets the No Location dialog instead, whose "here" opens the account (where Create Location is).
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Button, Dialog, TextField, useToast } from '@radish/ui-web';
import { unwrap, useApi, type CallFailure } from '../../data/api.tsx';
import { uuidv7 } from '../../lib/ids.ts';
import { EMAIL_INVALID, EMAIL_PATTERN, EMAIL_REQUIRED } from '../../lib/email.ts';
import { MATERIAL_WARNING_AMBER, MaterialIcon } from '../../shell/material-icons.tsx';


export interface InviteTarget {
  readonly accountId: string;
  readonly name: string;
  /** Whether the account has a location yet (legacy invites only then). */
  readonly hasLocation: boolean;
}

export function AccountInviteDialog({ target, onClose }: { readonly target: InviteTarget | null; readonly onClose: () => void }) {
  const toast = useToast();
  const { client } = useApi();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const send = useMutation<unknown, CallFailure, { accountId: string; email: string }>({
    mutationFn: (input) => {
      const commandId = uuidv7();
      const inviteId = uuidv7();
      return unwrap(() => client.commands.identity.inviteAccountUser({ inviteId: inviteId as never, accountId: input.accountId as never, email: input.email, delivery: 'email' }, { commandId }));
    },
  });
  const close = () => {
    setEmail('');
    setError(undefined);
    send.reset();
    onClose();
  };

  if (target !== null && !target.hasLocation) {
    return (
      <Dialog open onOpenChange={(o) => { if (!o) close(); }} title="No Location" testId="no-location-dialog" phone="inset" persistent={false} footer={<Button variant="flat" className="!h-10" onClick={close}>OK</Button>}>
        <p className="text-[14px] text-text-1">
          Before you can send an invite you need to create at least 1 Location. Click{' '}
          <Link to="/accounts/$accountId" params={{ accountId: target.accountId }} onClick={close} className="text-primary underline">here</Link>{' '}
          to create a location.
        </p>
      </Dialog>
    );
  }

  const trimmed = email.trim();
  const valid = EMAIL_PATTERN.test(trimmed);
  const submit = () => {
    if (target === null) return;
    if (!valid) return setError(trimmed === '' ? EMAIL_REQUIRED : EMAIL_INVALID);
    send.mutate({ accountId: target.accountId, email: trimmed.toLowerCase() }, {
      onSuccess: () => {
        toast.success('Invite sent successfully');
        close();
      },
      onError: (e) => (e.code === 'identity.already_member' || e.code === 'identity.already_invited' ? setError(e.message) : toast.error(e.message)),
    });
  };

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(o) => { if (!o) close(); }}
      title="Account User Invite"
      testId="account-invite-dialog"
      // Legacy InviteUser's UiCard is `width: 500px` in a bare q-dialog (measured 500 at 599 and 1024 on GG LA).
      width={500}
      phone="inset"
      footer={
        <>
          {/* Legacy BaseButton outline `color="font-tertiary"` (light #8c8d8c, under the AA default text-3) and `color="purple"`
              (B1322); legacy's dark BaseButton draws both in its own dark colours (Cancel primary, Invite #c59afe on black).
              Invite passes `:disabled`, not BaseButton's `disable`, so it is Quasar's `[disabled]` at opacity 0.6, not q-btn's 0.7. */}
          <Button variant="outline" className="!text-text-3 dark:!text-primary" onClick={close}>Cancel</Button>
          <Button className="!bg-purple-button !text-purple-button-ink data-[disabled]:!opacity-60" onClick={submit} disabled={!valid} loading={send.isPending}>Invite</Button>
        </>
      }
    >
      <form className="flex flex-col gap-4" autoComplete="off" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div>
          <p className="text-[16px] leading-7 font-bold text-text-1">Account: {target?.name}</p>
          <p className="text-[14px] text-text-3">Send an email invitation to create an account user profile.</p>
        </div>
        <TextField label="Email Address" type="email" value={email} onValueChange={(v) => { setEmail(v); setError(undefined); }} error={error} autoComplete="off" />
        {/* Legacy's dense q-banner `.ui-account-invite-banner-purple` (B1322): padded 8, a 1 px --q-brand-purple border, radius
            8 and the brand purple at 10 %; its warning_amber avatar is 28 px brand purple with q-pr-sm (a 36 column), then the
            text 14/20 in --q-text-primary, and q-my-md's 16 below. Measured on GG LA at 1024: 452 x 78, light and dark. */}
        <div className="mb-4 flex items-start gap-2 rounded-q border border-brand-purple bg-brand-purple-lighter p-2" data-testid="account-invite-banner">
          <span className="flex w-9 shrink-0 text-brand-purple"><MaterialIcon path={MATERIAL_WARNING_AMBER} size={28} /></span>
          <p className="text-[14px] leading-5 text-text-1">
            This invite is for account users only. After accepting the invite and creating a profile, this user will have access to all locations associated with the account.
          </p>
        </div>
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}
